/**
 * AUTO-ADVANCE ONBOARDING
 *
 * Watches world/review state and automatically completes onboarding
 * steps when the user performs the corresponding action.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import {
  useOnboardingStore,
  useShouldShowChecklist,
} from "@/stores/onboardingStore";
import { useWorldStore } from "@/stores/worldStore";
import { useReviewStore } from "@/stores/reviewStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";

export function useOnboardingAutoAdvance() {
  const shouldShow = useShouldShowChecklist();
  const completeStep = useOnboardingStore((s) => s.completeStep);

  const workspaceCount = useWorkspaceStore((s) => s.workspaces.length);
  const projects = useWorldStore((s) => s.projects);
  const tasks = useWorldStore((s) => s.tasks);
  const runs = useReviewStore((s) => s.runs);

  const [, setLocation] = useLocation();

  const prevWorkspaces = useRef(workspaceCount);
  const prevProjects = useRef(projects.length);
  const prevTasks = useRef(tasks.length);
  const prevRuns = useRef(runs.length);
  const didInit = useRef(false);

  // On mount: auto-complete steps based on existing data
  useEffect(() => {
    if (didInit.current || !shouldShow) return;
    didInit.current = true;

    if (workspaceCount > 0) completeStep("create_workspace");
    if (projects.length > 0) completeStep("create_project");
    if (tasks.length > 0) completeStep("add_task");
    if (runs.length > 0) completeStep("run_agent");

    // User with a workspace is not new — auto-dismiss
    if (workspaceCount > 0) {
      useOnboardingStore.getState().dismiss();
    }
  }, [shouldShow, workspaceCount, projects.length, tasks.length, runs.length, completeStep]);

  // Catch-all: dismiss as soon as workspaces load (async), regardless of projects/tasks
  useEffect(() => {
    if (!shouldShow) return;
    if (workspaceCount > 0) {
      useOnboardingStore.getState().dismiss();
    }
  }, [shouldShow, workspaceCount]);

  // Detect 0 → >0 transitions for workspaces (loads async after mount)
  useEffect(() => {
    if (!shouldShow) return;
    if (prevWorkspaces.current === 0 && workspaceCount > 0) {
      completeStep("create_workspace");
    }
    prevWorkspaces.current = workspaceCount;
  }, [workspaceCount, shouldShow, completeStep]);

  // Detect 0 → >0 transitions for projects
  useEffect(() => {
    if (!shouldShow) return;
    if (prevProjects.current === 0 && projects.length > 0) {
      completeStep("create_project");
      setLocation("/projects");
    }
    prevProjects.current = projects.length;
  }, [projects.length, shouldShow, completeStep, setLocation]);

  // Detect 0 → >0 transitions for tasks
  useEffect(() => {
    if (!shouldShow) return;
    if (prevTasks.current === 0 && tasks.length > 0) {
      completeStep("add_task");
    }
    prevTasks.current = tasks.length;
  }, [tasks.length, shouldShow, completeStep]);

  // Detect 0 → >0 transitions for runs
  useEffect(() => {
    if (!shouldShow) return;
    if (prevRuns.current === 0 && runs.length > 0) {
      completeStep("run_agent");
      setLocation("/");
    }
    prevRuns.current = runs.length;
  }, [runs.length, shouldShow, completeStep, setLocation]);
}
